/**
 * Shared TypeScript types — mirror of the backend schema.
 */

export type TaskStatus =
  | "backlog"
  | "todo"
  | "in_progress"
  | "verifying"
  | "needs_input"
  | "in_review"
  | "done"
  | "interrupted";

export const TASK_STATUSES: readonly TaskStatus[] = [
  "backlog",
  "todo",
  "in_progress",
  "needs_input",
  "in_review",
  "done",
  "interrupted",
] as const;

export type TrackStatus =
  | "planning"
  | "ready"
  | "in_progress"
  | "needs_input"
  | "in_review"
  | "done"
  | "interrupted";

export type TaskKind = "implement" | "validate" | "spec" | "verify";
export type AutonomyLevel = "agent" | "read_only";
export const AUTONOMY_LEVELS: readonly AutonomyLevel[] = ["agent", "read_only"] as const;

export type ValidationVerdict = "confirmed" | "refuted" | "inconclusive";
export type SecretScanStatus = "unknown" | "clean" | "blocked" | "skipped";

export const TASK_RISKS = ["low", "medium", "high"] as const;
export type TaskRisk = (typeof TASK_RISKS)[number];

export const TASK_TYPES = [
  "bug",
  "feature",
  "docs",
  "test",
  "refactor",
  "chore",
  "security",
] as const;
export type TaskType = (typeof TASK_TYPES)[number];

export type TriageStatus =
  | "untriaged"
  | "triaging"
  | "agent_ready"
  | "needs_human";
export type TriagedBy = "manager" | "human";
export type DispatchOrigin = "manual" | "loop" | "track";

export type DocType =
  | "agents_md"
  | "masterplan"
  | "decisions"
  | "pending"
  | "bugs"
  | "changelog";

export type CliBinary = "claude" | "codex";

export type ChatRole = "user" | "assistant";

export interface Project {
  id: string;
  name: string;
  description: string | null;
  host: string;
  ssh_user: string;
  ssh_key_path: string;
  repo_path: string;
  cli_binary: CliBinary;
  cli_flags: string;
  default_autonomy_level: AutonomyLevel;
  default_model: string | null;
  worker_model: string | null;
  fallback_models: string[];
  use_worktrees: boolean;
  secret_scan_enabled: boolean;
  active: boolean;
  // Autonomous-loop controls.
  manager_loop_enabled: boolean;
  worker_loop_enabled: boolean;
  security_scan_enabled: boolean;
  max_concurrent_auto: number;
  test_command: string | null;
  agent_user: string | null;
  auto_merge: boolean;
  allowed_automerge_types: string[];
  triage_requires_confirmation: boolean;
  risk_autonomy_policy: Record<string, unknown>;
  quota_pause_threshold: number;
  auto_token_window_budget: number | null;
  display_order: number;
  created_at: string;
}

export interface PromptHistoryEntry {
  prompt: string;
  authored_by: "user" | "suggester";
  authored_at: string;
}

export interface SpawnedProposal {
  title: string;
  description?: string | null;
  prompt?: string | null;
  priority?: TaskPriority | null;
  reason?: string | null;
  // The proposal gate: what kind of work, what shows it is real, where it
  // came from, which existing tasks it resembles, and whether it is parked
  // as an idea (no evidence, or new scope) until the operator accepts it.
  kind?: string | null;
  evidence?: string | null;
  source?: string | null;
  similar?: { task_id: string; title: string; status: string }[] | null;
  parked?: boolean | null;
  parked_why?: string | null;
}

export interface Idea {
  task_id: string;
  task_title: string;
  project_id: string;
  index: number;
  proposal: SpawnedProposal;
}


export interface Task {
  id: string;
  project_id: string;
  track_id: string | null;
  track_ordinal: number | null;
  track_milestone: number | null;
  parent_task_id: string | null;
  pending_spawn_proposals: SpawnedProposal[];
  title: string;
  description: string | null;
  prompt: string | null;
  prompt_history: PromptHistoryEntry[];
  status: TaskStatus;
  kind: TaskKind;
  agent_type: string | null;
  model: string | null;
  autonomy_level: AutonomyLevel;
  spec_mode: boolean;
  spec_approved: boolean;
  worktree_name: string | null;
  cli_session_id: string | null;
  validation_verdict: ValidationVerdict | null;
  validation_notes: string | null;
  secret_scan_status: SecretScanStatus;
  secret_scan_findings: { findings: SecretFinding[] } | null;
  session_log: string | null;
  session_log_archived_bytes: number;
  source_slug: string | null;
  source_date: string | null;
  priority: TaskPriority | null;
  lifecycle: "active" | "shelved";
  send_back_count: number;
  attempt_count: number;
  tier: "tiny" | "normal" | "large";
  acceptance_criteria: string | null;
  // Tokens processed per phase: setup / work / wrapup.
  token_split: Record<string, number>;
  confirmed_done_at: string | null;
  docs_synced_at: string | null;
  merged_at: string | null;
  checkpoint_data: CheckpointData | null;
  tokens_used: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: string;
  // Triage (manager loop). cost_usd is a display-only estimate.
  risk: TaskRisk | null;
  task_type: TaskType | null;
  triage_status: TriageStatus;
  triage_notes: string | null;
  triaged_at: string | null;
  triaged_by: TriagedBy | null;
  triage_confidence: string | null;
  auto_dispatch: boolean;
  auto_attempts: number;
  dispatch_origin: DispatchOrigin;
  started_at: string | null;
  completed_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ModelChoice {
  id: string;
  label: string;
  cli_binary: string;
  default: boolean;
  auth_method?: string | null;
  is_alias?: boolean;
  builtin?: boolean;
  // Reasoning-effort levels this model offers, in dropdown order. Empty means
  // no effort control (Haiku, the Codex no-model sentinel). The chosen level is
  // stored on the model value as a "@<effort>" suffix.
  efforts?: string[];
  default_effort?: string | null;
}

export const TASK_PRIORITIES = ["P0", "P1", "P2", "P3"] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export interface DoneFlowResult {
  status: "ok" | "skipped" | "partial" | "error";
  pending_edited: boolean;
  changelog_appended: boolean;
  committed: boolean;
  pushed: boolean;
  message: string;
}

export interface MergeBranchResult {
  status: "ok" | "skipped" | "partial" | "conflict" | "error" | "queued";
  branch: string;
  main_before: string | null;
  main_after: string | null;
  branch_deleted: boolean;
  pushed: boolean;
  message: string;
  conflicted_files?: string[];
}

export interface TaskConfirmOutcome {
  task_id: string;
  merged: boolean;
  docs_status: string;
  message: string;
}

export interface BatchConfirmResult {
  tasks: TaskConfirmOutcome[];
  message: string;
}

export type EvidenceVerdict = "pass" | "fail" | "misconfigured" | "inconclusive";

/** One check Maestro ran on a task's branch (or an operator override). */
export interface VerificationRecord {
  id: string;
  task_id: string;
  attempt: number;
  kind: "gate" | "mutation" | "verifier" | "secret_scan" | "override" | "scenario";
  command: string | null;
  exit_code: number | null;
  commit_sha: string | null;
  verdict: EvidenceVerdict;
  checked: string[];
  not_checked: string[];
  findings: unknown[];
  excerpt: string | null;
  log_ref: string | null;
  model: string | null;
  actor: string;
  reason: string | null;
  created_at: string;
}

export interface Verification {
  verification_verdict: string | null;
  verified_sha: string | null;
  fix_rounds: number;
  head_sha: string | null;
  head_verdict: string | null;
  mergeable: boolean;
  blocked: string[];
  criteria: { criterion: string; met: boolean }[];
  automerge_decision: "queued" | "shadow" | "vetoed" | null;
  automerge_block_reasons: string[];
  records: VerificationRecord[];
}

export interface ConfirmDoneResult {
  done: DoneFlowResult;
  merge: MergeBranchResult | null;
}

export interface BacklogSyncResult {
  status: "ok" | "skipped" | "partial" | "error";
  pending_edited: boolean;
  committed: boolean;
  pushed: boolean;
  slug: string | null;
  message: string;
}

export interface TaskCreated {
  task: Task;
  pending_sync: BacklogSyncResult | null;
}

export interface TaskHardDeleted {
  task_id: string;
  pending_sync: BacklogSyncResult | null;
}

/** The Needs Input decision card (GET /tasks/{id}/decision-card). */
export interface DecisionCard {
  attempt: string;
  stop: "review_failed" | "merge_conflict" | "merge_checks_failed" | "setup_broken" | "agent_asked" | "crashed" | "usage";
  title: string;
  purpose: string;
  need: string;
  progress: { label: string; ok: boolean; detail: string | null }[];
  why: string;
  points_heading: string;
  points: { text: string; severity: string | null }[];
  options: {
    key: string;
    label: string;
    consequence: string;
    recommended: boolean;
    needs_reason: boolean;
  }[];
  instruct_label: string;
  project: string;
  history: string;
  origin: string;
}

export interface CheckpointData {
  blocker: string;
  options?: string[];
  completed: string[];
  raised_at: string;
}

export interface SecretFinding {
  rule: string;
  description: string;
  file: string | null;
  line: number | null;
  excerpt: string;
  confidence: "low" | "medium" | "high";
}

export interface DocSync {
  id: string;
  project_id: string;
  doc_type: DocType;
  content: string | null;
  synced_at: string;
}


export interface CostSummary {
  project_id: string;
  project_name: string;
  tokens_total: number;
  cost_usd: string;
  cache_read_total: number;
  cache_creation_total: number;
  by_model: Record<
    string,
    { tokens: number; cost_usd: string; cache_read: number; cache_creation: number }
  >;
}

export interface CostEstimate {
  tokens: number;
  cost_usd: string;
  confidence: "low" | "medium" | "high";
  sample_size: number;
}

export interface TrackFeature {
  title: string;
  prompt: string;
  ordinal: number;
}

export interface TrackMilestone {
  title: string;
  features: TrackFeature[];
  acceptance: string[];
}

export interface TrackPlan {
  milestones: TrackMilestone[];
}

export interface Track {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  plan: TrackPlan;
  status: TrackStatus;
  models: Record<string, string>;
  autonomy_level: AutonomyLevel;
  parallel: boolean;
  shared_session: boolean;
  cost_estimate_usd: string;
  cost_actual_usd: string;
  tokens_used: number;
  active_milestone: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NewMilestoneSpec {
  title: string;
  acceptance: string[];
}

export interface AttachTaskRequest {
  /** Join an existing milestone by index … */
  milestone_idx?: number;
  /** … or append a fresh milestone for this task. Provide exactly one. */
  new_milestone?: NewMilestoneSpec;
  /** Extra acceptance criteria to add to the chosen milestone. */
  acceptance?: string[];
}

export interface BulkAttachTaskRequest {
  /** The un-started tasks to attach, all into the same destination. */
  task_ids: string[];
  milestone_idx?: number;
  new_milestone?: NewMilestoneSpec;
  acceptance?: string[];
}

export interface ProjectSkill {
  id: string;
  project_id: string | null;
  name: string;
  description: string;
  body: string;
  kind: "builtin" | "markdown" | "executable";
  source: "skills" | "commands" | null;
  synced_at: string;
}

export interface SlashCommandResult {
  name: string;
  rendered: string;
  side_effects: string[];
}

export interface SearchHit {
  task_id: string;
  project_id: string;
  title: string;
  snippet: string;
  rank: number;
  status: TaskStatus;
  created_at: string;
}

export interface SearchResponse {
  query: string;
  hits: SearchHit[];
  took_ms: number;
}

export interface TaskProposal {
  title: string;
  project_id: string;
  prompt: string;
  rationale?: string;
}

export interface TrackProposal {
  title: string;
  project_id: string;
  description?: string;
  plan: TrackPlan;
  models: Record<string, string>;
  autonomy_level: AutonomyLevel;
}

export interface ChatMessage {
  role: ChatRole;
  content: string;
  timestamp: string;
  task_proposal?: TaskProposal;
  track_proposal?: TrackProposal;
}

export interface ChatThreadSummary {
  id: string;
  project_id: string | null;
  project_name: string | null;
  title: string;
  created_at: string;
  updated_at: string;
  last_message_at: string;
  message_count: number;
  last_message_preview: string | null;
}

export interface ChatMessageRow {
  id: string;
  thread_id: string;
  role: ChatRole;
  content: string;
  task_proposal?: TaskProposal | null;
  track_proposal?: TrackProposal | null;
  created_at: string;
}

export interface ChatThreadDetail {
  id: string;
  project_id: string | null;
  title: string;
  created_at: string;
  updated_at: string;
  last_message_at: string;
  messages: ChatMessageRow[];
}

export interface WsTaskOutputEvent {
  type: "task_output";
  task_id: string;
  chunk: string;
  ts: string;
}

export interface WsTaskStatusEvent {
  type: "task_status";
  task_id: string;
  status: TaskStatus;
  ts: string;
}

export interface WsTaskCheckpointEvent {
  type: "task_checkpoint";
  task_id: string;
  data: CheckpointData;
  ts: string;
}

export interface WsTaskCostEvent {
  type: "task_cost";
  task_id: string;
  tokens_used: number;
  cost_usd: string;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  ts: string;
}

export interface WsTaskIdleEvent {
  type: "task_idle";
  task_id: string;
  idle_seconds: number;
  ts: string;
}

export interface WsTaskLoopEvent {
  type: "task_loop";
  task_id: string;
  pattern: string;
  occurrences: number;
  ts: string;
}

export type AgentEventKind =
  | "session_start"
  | "text"
  | "tool_call"
  | "tool_result"
  | "usage"
  | "result";

export interface AgentEvent {
  id?: string;
  task_id?: string;
  ordinal: number;
  kind: AgentEventKind;
  payload: Record<string, unknown>;
  created_at?: string;
}

export interface WsAgentEvent {
  type: "agent_event";
  task_id: string;
  ordinal: number;
  kind: AgentEventKind;
  payload: Record<string, unknown>;
  ts: string;
}

export interface WsTaskSpawnProposalsEvent {
  type: "task_spawn_proposals";
  task_id: string;
  proposals: SpawnedProposal[];
  ts: string;
}

export type WebSocketEvent =
  | WsTaskOutputEvent
  | WsTaskStatusEvent
  | WsTaskCheckpointEvent
  | WsTaskCostEvent
  | WsTaskIdleEvent
  | WsTaskLoopEvent
  | WsAgentEvent
  | WsTaskSpawnProposalsEvent;

export interface Theme {
  id: string;
  name: string;
  vars: Record<string, string>;
}
